import type {
  DiscordActivityResolveRequest,
  DiscordActivityResolveResponse,
  DiscordActivityOpsSnapshotResponse,
  DiscordActivityFloorManagerOnDutyResponse,
  DiscordActivityAdminReleaseJobRequest,
  DiscordActivityAdminTerminateJobRequest,
  DiscordActivityAdminJobMutationResponse,
  DiscordActivityAdminBreakRequestDetail,
  TesterQueueResponse,
  ActiveJobResponse,
  TesterPresenceResponse,
  TesterConfigResponse,
  PublicTesterProfile,
  TesterMergedScheduleResponse,
  TesterRecentJobsResponse,
  BreakRequestResponse,
  BreakRequestProposalsListResponse,
  CounterOfferBreakRequest,
  DecideBreakRequest,
} from '@runhuman/shared';

import { apiPrefix } from './api-prefix';

interface RequestInitWithToken extends RequestInit {
  accessToken?: string;
}

async function request<TResponse>(
  path: string,
  init: RequestInitWithToken = {},
): Promise<TResponse> {
  const headers = new Headers(init.headers);
  if (init.accessToken) {
    headers.set('Authorization', `Bearer ${init.accessToken}`);
  }
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${apiPrefix}${path}`, { ...init, headers });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`${path} failed: ${response.status} ${body}`);
  }

  return (await response.json()) as TResponse;
}

export async function resolveActivitySession(
  body: DiscordActivityResolveRequest,
): Promise<DiscordActivityResolveResponse> {
  return request('/discord/activity/resolve', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function fetchTesterQueue(accessToken: string): Promise<TesterQueueResponse> {
  return request('/discord/activity/queue', { method: 'GET', accessToken });
}

export async function fetchActiveJob(accessToken: string): Promise<ActiveJobResponse> {
  return request('/discord/activity/active-job', { method: 'GET', accessToken });
}

export async function fetchTesterPresence(
  accessToken: string,
): Promise<TesterPresenceResponse> {
  return request('/discord/activity/presence', { method: 'GET', accessToken });
}

export async function fetchActivityConfig(
  accessToken: string,
): Promise<TesterConfigResponse> {
  return request('/discord/activity/config', { method: 'GET', accessToken });
}

export async function fetchOpsSnapshot(
  accessToken: string,
): Promise<DiscordActivityOpsSnapshotResponse> {
  return request('/discord/activity/ops-snapshot', { method: 'GET', accessToken });
}

export async function fetchFloorManagerOnDuty(
  accessToken: string,
): Promise<DiscordActivityFloorManagerOnDutyResponse> {
  return request('/discord/activity/floor-manager-on-duty', {
    method: 'GET',
    accessToken,
  });
}

export async function releaseJobAdmin(
  accessToken: string,
  jobId: string,
  body: DiscordActivityAdminReleaseJobRequest = {},
): Promise<DiscordActivityAdminJobMutationResponse> {
  return request(`/discord/activity/jobs/${jobId}/release`, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

export async function terminateJobAdmin(
  accessToken: string,
  jobId: string,
  body: DiscordActivityAdminTerminateJobRequest = {},
): Promise<DiscordActivityAdminJobMutationResponse> {
  return request(`/discord/activity/jobs/${jobId}/terminate`, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

// ─── Tester profile dialog (#3967) ───────────────────────────────────────

export async function fetchActivityTesterPublicProfile(
  accessToken: string,
  testerId: string,
): Promise<PublicTesterProfile> {
  return request(`/discord/activity/testers/${testerId}/profile`, {
    method: 'GET',
    accessToken,
  });
}

export async function fetchActivityTesterPublicSchedule(
  accessToken: string,
  testerId: string,
  startDate: string,
  endDate: string,
): Promise<TesterMergedScheduleResponse> {
  const qs = new URLSearchParams({ startDate, endDate }).toString();
  return request(`/discord/activity/testers/${testerId}/schedule?${qs}`, {
    method: 'GET',
    accessToken,
  });
}

export async function fetchActivityTesterRecentJobs(
  accessToken: string,
  testerId: string,
): Promise<TesterRecentJobsResponse> {
  return request(`/discord/activity/testers/${testerId}/recent-jobs`, {
    method: 'GET',
    accessToken,
  });
}

// ─── Admin break-request drawer + inline-resolve (#4201) ─────────────────

export async function fetchAdminBreakRequestDetail(
  accessToken: string,
  requestId: string,
): Promise<DiscordActivityAdminBreakRequestDetail> {
  return request(`/discord/activity/break-requests/${requestId}`, {
    method: 'GET',
    accessToken,
  });
}

export async function decideAdminBreakRequest(
  accessToken: string,
  requestId: string,
  body: DecideBreakRequest,
): Promise<BreakRequestResponse> {
  return request(`/discord/activity/break-requests/${requestId}/decide`, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

/**
 * Counter-offer from the iframe admin drawer (#4258). Mirrors the web
 * admin's `counterOfferAsAdmin` byte-for-byte at the service layer — server
 * routes through the same `BreakRequestService.proposeAlternative({ actor:
 * 'admin' })` and stamps `actorSource: 'panel'` on the audit fan-out, so
 * which surface the admin used disappears past the wire.
 */
export async function counterOfferAdminBreakRequest(
  accessToken: string,
  requestId: string,
  body: CounterOfferBreakRequest,
): Promise<BreakRequestResponse> {
  return request(`/discord/activity/break-requests/${requestId}/counter`, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

/**
 * Per-request audit chain (#3772, surfaced on iframe in #4258). Returns every
 * round in `(roundIndex ASC)` order so the drawer's `<BreakProposalChain />`
 * widget renders the same negotiation timeline as the web admin panel.
 */
export async function listAdminBreakRequestProposals(
  accessToken: string,
  requestId: string,
): Promise<BreakRequestProposalsListResponse> {
  return request(`/discord/activity/break-requests/${requestId}/proposals`, {
    method: 'GET',
    accessToken,
  });
}
